/**
 * Signal-Grade Double Ratchet wrapper around `2key-ratchet`.
 *
 * Provides:
 *  - Identity creation (X3DH identity + signed prekey + one-time prekeys)
 *  - Session establishment (initiator & responder)
 *  - Per-message encrypt/decrypt with automatic ratchet advancement
 *  - Serialisation helpers for IndexedDB persistence
 */

import {
  Identity,
  AsymmetricRatchet,
  PreKeyBundleProtocol,
  PreKeyMessageProtocol,
  MessageSignedProtocol,
} from '2key-ratchet';
import type { IJsonIdentity } from '2key-ratchet';
import { RemoteIdentity } from '2key-ratchet';
import {
  storeSession,
  loadSession,
  storeIdentity,
  loadIdentity,
} from './ratchet-session-store';
// Re-export for external use
export { PreKeyBundleProtocol, PreKeyMessageProtocol };

// ─── Helpers ────────────────────────────────────────────────

import { arrayBufferToBase64 as ab2b64, base64ToArrayBuffer as b642ab } from './crypto-utils';
export { b642ab };

// ─── Identity management ────────────────────────────────────

/**
 * Create a fresh X3DH identity with signed prekeys and one-time prekeys.
 */
export async function createIdentity(
  userId: string,
  signedPreKeyCount = 1,
  oneTimePreKeyCount = 100,
): Promise<Identity> {
  const idNum = hashUuidToInt(userId);
  const identity = await Identity.create(idNum, signedPreKeyCount, oneTimePreKeyCount);
  const json = await identity.toJSON();
  await storeIdentity(userId, json);
  return identity;
}

/**
 * Load an existing identity from IndexedDB, or return null.
 */
export async function getIdentity(userId: string): Promise<Identity | null> {
  const json = await loadIdentity(userId);
  if (!json) return null;
  try {
    return await Identity.fromJSON(json as IJsonIdentity);
  } catch (err) {
    console.error('[DoubleRatchet] Failed to restore identity:', err);
    return null;
  }
}

/**
 * Get or create the user's ratchet identity.
 */
export async function getOrCreateIdentity(userId: string): Promise<Identity> {
  const existing = await getIdentity(userId);
  if (existing) return existing;
  return createIdentity(userId);
}

// ─── PreKey bundle export ───────────────────────────────────

/**
 * Export the identity's prekey bundle as a serialised protobuf (base64).
 * This is what gets stored in the `user_prekeys` table.
 */
export async function exportPreKeyBundleBlob(identity: Identity): Promise<string> {
  const bundle = new PreKeyBundleProtocol();
  bundle.registrationId = identity.id;

  // Fill identity protocol
  type RatchetModule = Record<string, unknown> & { default?: Record<string, unknown> };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ratchetMod = await import('2key-ratchet') as unknown as RatchetModule & { IdentityProtocol: any };
  bundle.identity = await ratchetMod.IdentityProtocol.fill(identity);

  // Fill signed prekey using the protocol classes
  const mod = await import('2key-ratchet') as unknown as RatchetModule;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const PreKeySignedProto: any = mod.PreKeySignedProtocol || (mod.default as RatchetModule | undefined)?.PreKeySignedProtocol;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const PreKeyProto: any = mod.PreKeyProtocol || (mod.default as RatchetModule | undefined)?.PreKeyProtocol;

  if (PreKeySignedProto) {
    const signedPK = new PreKeySignedProto();
    signedPK.id = 0;
    signedPK.key = identity.signedPreKeys[0].publicKey;
    await signedPK.sign(identity.signingKey.privateKey);
    bundle.preKeySigned = signedPK;
  }

  // Fill one-time prekey (first available)
  if (identity.preKeys.length > 0 && PreKeyProto) {
    const otpk = new PreKeyProto();
    otpk.id = 0;
    otpk.key = identity.preKeys[0].publicKey;
    bundle.preKey = otpk;
  }

  const raw = await bundle.exportProto();
  return ab2b64(raw);
}

/**
 * Import a prekey bundle from its serialised protobuf (base64).
 */
export async function importPreKeyBundleBlob(blob: string): Promise<PreKeyBundleProtocol> {
  const raw = b642ab(blob);
  return await PreKeyBundleProtocol.importProto(raw) as PreKeyBundleProtocol;
}

/**
 * Export identity key as base64 (for storing in encryption_keys.identity_key).
 */
export async function exportIdentityKey(identity: Identity): Promise<string> {
  const signingPubRaw = await crypto.subtle.exportKey('spki', identity.signingKey.publicKey.key);
  return ab2b64(signingPubRaw);
}

// ─── Session creation ───────────────────────────────────────

/**
 * Initiator: Create a ratchet session using the recipient's prekey bundle.
 */
export async function initiateSession(
  myIdentity: Identity,
  myUserId: string,
  otherUserId: string,
  theirBundle: PreKeyBundleProtocol,
): Promise<AsymmetricRatchet> {
  const cipher = await AsymmetricRatchet.create(myIdentity, theirBundle, { exportableKeys: true });

  cipher.on('update', async () => {
    await persistSession(myUserId, otherUserId, cipher);
  });

  await persistSession(myUserId, otherUserId, cipher);
  return cipher;
}

/**
 * Responder: Accept an incoming PreKeyMessage to establish a session.
 */
export async function acceptSession(
  myIdentity: Identity,
  myUserId: string,
  otherUserId: string,
  preKeyMessage: PreKeyMessageProtocol,
): Promise<AsymmetricRatchet> {
  const cipher = await AsymmetricRatchet.create(myIdentity, preKeyMessage, { exportableKeys: true });

  cipher.on('update', async () => {
    await persistSession(myUserId, otherUserId, cipher);
  });

  await persistSession(myUserId, otherUserId, cipher);
  return cipher;
}

// ─── Encrypt / Decrypt ──────────────────────────────────────

export interface RatchetEncryptResult {
  ciphertext: string;        // base64 of the encrypted protobuf
  isPreKeyMessage: boolean;  // true for the first message (includes X3DH handshake)
}

/**
 * Encrypt a plaintext string, advancing the sending ratchet.
 */
export async function ratchetEncrypt(
  cipher: AsymmetricRatchet,
  plaintext: string,
): Promise<RatchetEncryptResult> {
  const data = new TextEncoder().encode(plaintext);
  const proto = await cipher.encrypt(data.buffer as ArrayBuffer);
  const raw = await proto.exportProto();
  const isPreKey = proto instanceof PreKeyMessageProtocol;
  return {
    ciphertext: ab2b64(raw),
    isPreKeyMessage: isPreKey,
  };
}

/**
 * Decrypt a ciphertext string, advancing the receiving ratchet.
 */
export async function ratchetDecrypt(
  cipher: AsymmetricRatchet,
  ciphertextB64: string,
): Promise<string> {
  const raw = b642ab(ciphertextB64);

  // Try to parse as PreKeyMessage first
  let proto: MessageSignedProtocol;
  try {
    const preKeyProto = await PreKeyMessageProtocol.importProto(raw);
    proto = (preKeyProto as PreKeyMessageProtocol).signedMessage;
  } catch {
    proto = await MessageSignedProtocol.importProto(raw) as MessageSignedProtocol;
  }

  const decrypted = await cipher.decrypt(proto);
  return new TextDecoder().decode(decrypted);
}

// ─── Session persistence helpers ────────────────────────────

async function persistSession(
  myUserId: string,
  otherUserId: string,
  cipher: AsymmetricRatchet,
): Promise<void> {
  try {
    const sessionJson = await cipher.toJSON();
    const remoteJson = await cipher.remoteIdentity.toJSON();
    await storeSession(myUserId, otherUserId, sessionJson, remoteJson);
  } catch (err) {
    console.error('[DoubleRatchet] Failed to persist session:', err);
  }
}

/**
 * Restore a previously persisted session from IndexedDB.
 */
export async function restoreSession(
  myUserId: string,
  otherUserId: string,
  myIdentity: Identity,
): Promise<AsymmetricRatchet | null> {
  const stored = await loadSession(myUserId, otherUserId);
  if (!stored) return null;

  try {
    const remote = await RemoteIdentity.fromJSON(stored.remoteIdentity);
    const cipher = await AsymmetricRatchet.fromJSON(
      myIdentity,
      remote,
      stored.session,
    );

    cipher.on('update', async () => {
      await persistSession(myUserId, otherUserId, cipher);
    });

    return cipher;
  } catch (err) {
    console.error('[DoubleRatchet] Failed to restore session:', err);
    return null;
  }
}

// ─── Utility ────────────────────────────────────────────────

function hashUuidToInt(uuid: string): number {
  let hash = 0;
  const str = uuid.replace(/-/g, '');
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + ch;
    hash |= 0;
  }
  return Math.abs(hash);
}
